import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, Row, Col, Card, ProgressBar, Button, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import Swal from "sweetalert2";

function P2PSender() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sentCount, setSentCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isFinishing, setIsFinishing] = useState(false);
  // isFlushing: true the instant the last message in a batch is pressed —
  // triggers the spinner before any async network call starts.
  const [isFlushing, setIsFlushing] = useState(false);
  // bufferCountdown: seconds remaining before auto-flush (null = buffer empty, no countdown active)
  const [bufferCountdown, setBufferCountdown] = useState(null);
  const [mediaPreviewIsVideo, setMediaPreviewIsVideo] = useState(false);

  const localQueueRef = useRef([]);
  const debounceTimerRef = useRef(null);
  const inFlightFlushPromiseRef = useRef(null);
  const sentMessageIdsRef = useRef(new Set());
  const lastSendTimeRef = useRef(0);
  const lastActivityRef = useRef(Date.now());    // Updated on every Enter press
  const countdownIntervalRef = useRef(null);     // setInterval handle for inactivity countdown
  const bufferCreatedAtRef = useRef(null);       // Tracks when the first message entered the current buffer

  // Fetch campaign details and the first batch of pending messages
  const fetchPendingData = useCallback(async (isInitial = false) => {
    try {
      // Strictly await any active batch dispatch before fetching pending messages
      if (inFlightFlushPromiseRef.current) {
        await inFlightFlushPromiseRef.current;
      }

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (isInitial) {
        const campRes = await axios.get(`${API_BASE_URL}/campaigns/${id}`, { headers });
        if (campRes.data.success) {
          setCampaign(campRes.data.campaign);
          setTotalCount(campRes.data.campaign.stats?.totalContacts || 0);
          const currentProgress = (campRes.data.campaign.stats?.sent || 0) + 
                                  (campRes.data.campaign.stats?.delivered || 0) +
                                  (campRes.data.campaign.stats?.failed || 0) +
                                  (campRes.data.campaign.stats?.bounced || 0);
          setSentCount(currentProgress);
        }
      }

      const pendingRes = await axios.get(`${API_BASE_URL}/campaigns/${id}/p2p-pending`, { headers });
      if (pendingRes.data.success) {
        // Filter out any messages that have already been queued or dispatched in this session
        const rawPending = pendingRes.data.pending || [];
        const filtered = rawPending.filter(m => !sentMessageIdsRef.current.has(m._id));
        setPendingMessages(filtered);
        setCurrentIndex(0);
      }
    } catch (err) {
      console.error("Error fetching P2P data:", err);
      Swal.fire("Error", "Failed to load pending campaign messages.", "error");
    } finally {
      setIsLoading(false);
      setIsFlushing(false); // clear spinner once fetch resolves
    }
  }, [id]);

  useEffect(() => {
    fetchPendingData(true);
  }, [fetchPendingData]);

  // Flush the buffered queue to the backend Twilio dispatch endpoint
  const flushQueue = useCallback(async () => {
    const idsToFlush = [...localQueueRef.current];
    if (idsToFlush.length === 0) {
      return inFlightFlushPromiseRef.current;
    }
    
    // Clear local buffer ref and reset timer immediately so newly queued messages enter a clean batch
    localQueueRef.current = [];
    bufferCreatedAtRef.current = null;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Mark IDs in active session set so they cannot be re-fetched or duplicated
    idsToFlush.forEach(mid => sentMessageIdsRef.current.add(mid));

    let currentPromise;
    const runDispatch = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        await axios.post(`${API_BASE_URL}/campaigns/${id}/p2p-send`, {
          messageIds: idsToFlush
        }, { headers });
      } catch (err) {
        console.error("Error flushing manual send batch:", err);
        // On failure: restore IDs to localQueue and unmark from sentMessageIdsRef
        // so no SMS is ever dropped!
        idsToFlush.forEach(mid => sentMessageIdsRef.current.delete(mid));
        localQueueRef.current.unshift(...idsToFlush);
      } finally {
        if (inFlightFlushPromiseRef.current === currentPromise) {
          inFlightFlushPromiseRef.current = null;
        }
      }
    };

    currentPromise = inFlightFlushPromiseRef.current
      ? inFlightFlushPromiseRef.current.then(runDispatch)
      : runDispatch();

    inFlightFlushPromiseRef.current = currentPromise;
    return currentPromise;
  }, [id]);

  // Buffer messages in a local queue and trigger flush on limits/timeouts
  const queueMessageForSend = useCallback((messageId) => {
    if (localQueueRef.current.length === 0) {
      bufferCreatedAtRef.current = Date.now();
    }
    localQueueRef.current.push(messageId);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (localQueueRef.current.length >= 20) {
      flushQueue();
    } else if (typeof window === "undefined" || !window.__P2P_DISABLE_DEBOUNCE__) {
      // Skip the 500ms debounce in E2E test mode so the inactivity timer
      // and sendBeacon can be tested with a controlled buffer state.
      debounceTimerRef.current = setTimeout(flushQueue, 500);
    }
  }, [flushQueue]);

  // Flush remaining buffer when leaving page
  useEffect(() => {
    return () => {
      if (localQueueRef.current.length > 0) {
        flushQueue();
      }
    };
  }, [flushQueue]);

  // ── 3-minute inactivity countdown ──────────────────────────────────────────
  // Fires every second. If the buffer has messages and user hasn't pressed Enter
  // for 3 minutes, auto-flushes and shows a persistent notification.
  // window.__P2P_INACTIVITY_TIMEOUT_MS__ can be set by E2E tests to speed up the timer.
  useEffect(() => {
    const INACTIVITY_MS = (typeof window !== "undefined" && window.__P2P_INACTIVITY_TIMEOUT_MS__)
      ? window.__P2P_INACTIVITY_TIMEOUT_MS__
      : 180000; // 3 minutes default

    countdownIntervalRef.current = setInterval(() => {
      const bufferedCount = localQueueRef.current.length;
      if (bufferedCount === 0 || !bufferCreatedAtRef.current) {
        setBufferCountdown(null);
        return;
      }
      const elapsed = Date.now() - bufferCreatedAtRef.current;

      // Don't show the indicator during normal sending — the 500ms debounce
      // flushes the buffer quickly, so elapsed stays well under this threshold.
      // Only reveal the countdown after genuine extended inactivity.
      const SHOW_AFTER_MS = (typeof window !== "undefined" && window.__P2P_INACTIVITY_TIMEOUT_MS__)
        ? Math.min(window.__P2P_INACTIVITY_TIMEOUT_MS__ * 0.1, 2000)  // 10% of timeout in test mode, min 2s
        : 10000; // 10 seconds in production

      if (elapsed < SHOW_AFTER_MS) {
        setBufferCountdown(null); // too early — don't flash the indicator
        return;
      }

      const remaining = Math.max(0, Math.floor(INACTIVITY_MS / 1000) - Math.floor(elapsed / 1000));
      setBufferCountdown(remaining);

      if (remaining === 0) {
        const count = localQueueRef.current.length;
        flushQueue();
        setBufferCountdown(null);
        // Persistent notification — user must click to dismiss
        Swal.fire({
          title: "\u23F1 Buffer Auto-Flushed",
          html: `<p><strong>${count}</strong> buffered message${count !== 1 ? "s were" : " was"} automatically sent after 3 minutes of inactivity.</p><p class="text-muted mb-0" style="font-size:0.9rem">The remaining campaign messages are still pending your manual approval.</p>`,
          icon: "info",
          confirmButtonText: "Close this notification",
          confirmButtonColor: "#004992",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
      }
    }, 1000);
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [flushQueue]);

  // ── sendBeacon rescue on page close ────────────────────────────────────────
  // navigator.sendBeacon fires even when the tab is closing. The backend beacon
  // endpoint validates the JWT from the request body (headers not settable).
  useEffect(() => {
    const handleBeforeUnload = () => {
      const ids = localQueueRef.current;
      if (ids.length === 0) return;
      const token = localStorage.getItem("token");
      const payload = JSON.stringify({ messageIds: ids, token });
      navigator.sendBeacon(
        `${API_BASE_URL}/p2p-beacon/${id}`,
        new Blob([payload], { type: "application/json" })
      );
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [id]);

  const handleSendCurrent = useCallback(() => {
    if (isLoading || isFinishing || pendingMessages.length === 0) return;
    if (currentIndex >= pendingMessages.length) return;

    const now = Date.now();
    // Throttling keyhold events to at most 1 message per 150ms
    if (now - lastSendTimeRef.current < 150) return;
    lastSendTimeRef.current = now;
    lastActivityRef.current = Date.now();  // Reset inactivity countdown

    const currentMsg = pendingMessages[currentIndex];
    if (!currentMsg) return;
    
    // 1. Instantly buffer send event in frontend state
    setSentCount(prev => Math.min(prev + 1, totalCount));
    queueMessageForSend(currentMsg._id);

    // 2. Advance index
    if (currentIndex + 1 < pendingMessages.length) {
      setCurrentIndex(prev => prev + 1);
    } else {
      // Last message in this batch — show spinner IMMEDIATELY before any async work
      setIsFlushing(true);
      setIsLoading(true);
      setCurrentIndex(pendingMessages.length); // Prevent any further dispatches while loading next page
      flushQueue().then(async () => {
        if (inFlightFlushPromiseRef.current) {
          await inFlightFlushPromiseRef.current;
        }
        await fetchPendingData(false);
      });
    }
  }, [isLoading, isFinishing, pendingMessages, currentIndex, totalCount, queueMessageForSend, fetchPendingData, flushQueue]);

  const handleSkipCurrent = useCallback(() => {
    if (isLoading || isFinishing || pendingMessages.length === 0) return;
    if (currentIndex >= pendingMessages.length) return;
    
    // Advance index without queueing for send
    if (currentIndex + 1 < pendingMessages.length) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setIsLoading(true);
      setCurrentIndex(pendingMessages.length); // Prevent any further dispatches while loading next page
      flushQueue().then(async () => {
        if (inFlightFlushPromiseRef.current) {
          await inFlightFlushPromiseRef.current;
        }
        await fetchPendingData(false);
      });
    }
  }, [isLoading, isFinishing, pendingMessages, currentIndex, fetchPendingData, flushQueue]);

  // Capture Enter keypress to send message
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSendCurrent();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSendCurrent]);

  if (isLoading && pendingMessages.length === 0) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <h5 className="text-muted fw-bold">Loading P2P Sending Console...</h5>
        </div>
      </Container>
    );
  }

  const activeMsg = pendingMessages[currentIndex];
  const hasFinished = pendingMessages.length === 0;

  return (
    <Container fluid className="py-4 px-5">
      {/* Header */}
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="fw-bold mb-1" style={{ fontWeight: 700 }}>Volunteer P2P Sending Console</h2>
            <p className="text-muted mb-0" style={{ fontWeight: 600 }}>
              {campaign?.name || "Loading Campaign Details..."}
            </p>
          </div>
          <Button
            variant="outline-secondary"
            className="fw-bold"
            style={{ borderRadius: "6px" }}
            onClick={() => {
              flushQueue().then(async () => {
                if (inFlightFlushPromiseRef.current) {
                  await inFlightFlushPromiseRef.current;
                }
                navigate("/admin/campaign-overview");
              });
            }}
          >
            Exit Console
          </Button>
        </Col>
      </Row>

      {hasFinished ? (
        // Finished View
        <Row className="justify-content-center mt-5">
          <Col md={6}>
            <Card className="text-center shadow-lg border-0 p-5" style={{ borderRadius: "16px" }}>
              <Card.Body>
                <div 
                  className="mx-auto mb-4 d-flex align-items-center justify-content-center text-white" 
                  style={{ width: "80px", height: "80px", borderRadius: "50%", backgroundColor: "#28a745", fontSize: "2.5rem" }}
                >
                  <i className="fas fa-check"></i>
                </div>
                <h3 className="fw-bold mb-3" style={{ fontWeight: 700 }}>All Messages Dispatched!</h3>
                <p className="text-muted mb-4" style={{ fontWeight: 600 }}>
                  You have cleared all pending P2P messages for this campaign. Thank you for your support!
                </p>
                <Button
                  className="fw-bold px-5 py-3 text-white border-0"
                  style={{ backgroundColor: "#004992", borderRadius: "8px" }}
                  onClick={() => navigate("/admin/campaign-overview")}
                >
                  Return to Campaigns
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        // Sending Console View
        <Row className="g-4">
          {/* Phone Mockup (Left) */}
          <Col lg={6} md={12} className="d-flex justify-content-center">
            <div 
              style={{
                width: "360px",
                height: "680px",
                border: "12px solid #222",
                borderRadius: "36px",
                backgroundColor: "#f7f7f7",
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                position: "relative"
              }}
            >
              {/* Phone Speaker */}
              <div 
                style={{
                  width: "150px",
                  height: "20px",
                  backgroundColor: "#222",
                  borderRadius: "0 0 12px 12px",
                  margin: "0 auto",
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10
                }}
              />
              
              {/* Phone Header */}
              <div 
                className="bg-white border-bottom pt-4 px-3 pb-2 text-center"
                style={{ height: "90px" }}
              >
                <div className="fw-bold text-dark mt-1" style={{ fontSize: "0.95rem" }}>
                  {activeMsg?.contactId?.firstName ? `${activeMsg.contactId.firstName} ${activeMsg.contactId.lastName || ""}` : activeMsg?.toPhoneNumber}
                </div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  SMS via {activeMsg?.fromPhoneNumber || "Unassigned"}
                </div>
              </div>

              {/* Message History Body */}
              <div 
                className="p-3 d-flex flex-column justify-content-end flex-grow-1"
                style={{ 
                  backgroundImage: "linear-gradient(to bottom, #f2f2f7, #ffffff)",
                  overflowY: "auto",
                  paddingBottom: "20px"
                }}
              >
                <div 
                  className="bg-primary text-white p-3 self-end ms-auto mb-2"
                  style={{
                    maxWidth: "85%",
                    borderRadius: "18px 18px 0px 18px",
                    fontSize: "0.85rem",
                    lineHeight: "1.35",
                    whiteSpace: "pre-line",
                    boxShadow: "0 2px 4px rgba(0,73,146,0.15)"
                  }}
                >
                  {activeMsg?.mediaUrl && (
                    <div className="mb-2">
                      {mediaPreviewIsVideo || /\.(mp4|mov|m4v)(\?|$)/i.test(activeMsg.mediaUrl) ? (
                        <video
                          src={activeMsg.mediaUrl}
                          controls
                          muted
                          playsInline
                          style={{ width: "100%", maxHeight: "160px", borderRadius: "8px", backgroundColor: "#000" }}
                        />
                      ) : (
                        <img
                          src={activeMsg.mediaUrl}
                          alt="MMS Attachment"
                          onError={() => setMediaPreviewIsVideo(true)}
                          style={{ width: "100%", maxHeight: "160px", objectFit: "contain", borderRadius: "8px" }}
                        />
                      )}
                    </div>
                  )}
                  {activeMsg?.body}
                </div>
              </div>

              {/* Phone Input Mock */}
              <div className="bg-white p-3 border-top d-flex align-items-center" style={{ height: "65px" }}>
                <div 
                  className="border rounded-pill px-3 py-1 text-muted flex-grow-1 bg-light"
                  style={{ fontSize: "0.8rem" }}
                >
                  iMessage
                </div>
                <i className="fas fa-arrow-circle-up text-primary ms-2" style={{ fontSize: "1.8rem" }}></i>
              </div>
            </div>
          </Col>

          {/* Console Controls (Right) */}
          <Col lg={6} md={12}>
            <Card className="shadow-sm border-0 h-100 p-4" style={{ borderRadius: "12px", border: "1px solid #c7c7c7" }}>
              <Card.Body className="d-flex flex-column justify-content-between">
                <div>
                  <h4 className="fw-bold mb-4" style={{ fontWeight: 700 }}>Interactive Queue Controls</h4>
                  
                  {/* Progress Tracker */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold text-muted" style={{ fontWeight: 700 }}>Campaign Sending Progress</span>
                      <span className="fw-bold text-primary" style={{ fontWeight: 700 }}>{sentCount} of {totalCount} ({totalCount > 0 ? Math.round((sentCount / totalCount) * 100) : 0}%)</span>
                    </div>
                    <ProgressBar 
                      now={totalCount > 0 ? (sentCount / totalCount) * 100 : 0} 
                      style={{ height: "12px", borderRadius: "10px" }}
                      variant="success"
                    />
                    {/* Inactivity countdown — only shown when buffer has unsent messages */}
                    {bufferCountdown !== null && (
                      <div
                        className="mt-2 d-flex align-items-center"
                        style={{ fontSize: "0.82rem", fontWeight: 600, color: bufferCountdown <= 30 ? "#c85a00" : "#6c757d" }}
                      >
                        <i className={`fas fa-hourglass-${bufferCountdown <= 30 ? "end" : "half"} me-2`} style={{ color: bufferCountdown <= 30 ? "#c85a00" : "#f0a500" }}></i>
                        Buffer: {localQueueRef.current.length} message{localQueueRef.current.length !== 1 ? "s" : ""} queued
                        {" — "}
                        auto-flushing in{" "}
                        <strong className="ms-1" style={{ color: bufferCountdown <= 30 ? "#c85a00" : "#f0a500" }}>
                          {Math.floor(bufferCountdown / 60)}:{String(bufferCountdown % 60).padStart(2, "0")}
                        </strong>
                      </div>
                    )}
                  </div>

                  <hr className="my-4" />

                  {/* Recipient Card */}
                  <h6 className="fw-bold text-muted mb-3" style={{ fontWeight: 700 }}>Current Recipient Info</h6>
                  <Card className="bg-light border-0 p-3 mb-4" style={{ borderRadius: "8px" }}>
                    <Row>
                      <Col xs={6}>
                        <div className="text-muted small" style={{ fontWeight: 600 }}>NAME</div>
                        <div className="fw-bold text-dark">{activeMsg?.contactId?.firstName ? `${activeMsg.contactId.firstName} ${activeMsg.contactId.lastName || ""}` : "Unnamed Contact"}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted small" style={{ fontWeight: 600 }}>PHONE</div>
                        <div className="fw-bold text-dark">{activeMsg?.toPhoneNumber}</div>
                      </Col>
                    </Row>
                  </Card>

                  {/* Operational Instructions */}
                  <div 
                    className="p-3 mb-4 rounded" 
                    style={{ backgroundColor: "#E6F4EA", border: "1px solid #c3e6cb", color: "#137333", fontSize: "0.9rem" }}
                  >
                    <i className="fas fa-keyboard me-2"></i>
                    <strong>P2P Tip:</strong> Press the <strong>Enter</strong> key on your keyboard to instantly send and load the next voter, or hold it down to send continuously.
                  </div>
                </div>

                {/* Dispatch Controls */}
                <div className="d-grid gap-3">
                  <Button
                    className="fw-bold py-3 text-white border-0"
                    style={{ backgroundColor: (isLoading || isFlushing) ? "#5a9e70" : "#28a745", borderRadius: "8px", fontSize: "1.1rem" }}
                    onClick={handleSendCurrent}
                    disabled={isLoading || isFlushing}
                  >
                    {(isLoading || isFlushing) ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Processing...
                      </>
                    ) : (
                      <><i className="fas fa-paper-plane me-2"></i>Send Message (Enter)</>
                    )}
                  </Button>
                  
                  {!(isLoading || isFlushing) && (
                    <div className="d-flex gap-3">
                      <Button
                        variant="outline-warning"
                        className="fw-bold py-2 w-50 d-flex align-items-center justify-content-center"
                        style={{
                          borderRadius: "8px",
                          border: "2px solid #ffc107",
                          color: "#c87f00",
                          backgroundColor: "transparent",
                          fontWeight: "600"
                        }}
                        onClick={handleSkipCurrent}
                      >
                        <i className="fas fa-forward me-2"></i>Skip Contact
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="fw-bold py-2 w-50 d-flex align-items-center justify-content-center"
                        style={{
                          borderRadius: "8px",
                          fontWeight: "600",
                          border: "2px solid #6c757d",
                          color: "#6c757d",
                          backgroundColor: "transparent"
                        }}
                        onClick={() => {
                          flushQueue().then(async () => {
                            if (inFlightFlushPromiseRef.current) {
                              await inFlightFlushPromiseRef.current;
                            }
                            navigate("/admin/campaign-overview");
                          });
                        }}
                      >
                        <i className="fas fa-times me-2"></i>Pause & Exit
                      </Button>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default P2PSender;
